import React from 'react';
import TopBar from '@/components/atelier/TopBar';
import Nav from '@/components/atelier/Nav';
import Footer from '@/components/atelier/Footer';
import EyebrowPill from '@/components/atelier/EyebrowPill';
import FinalCTACard from '@/components/atelier/FinalCTACard';
import { addJsonLd, buildServiceSchema, clearJsonLd, HAIRSALON_SCHEMA, getSiteSchemas } from '@/lib/seo';
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import DynamicServiceFAQ from '@/components/services/DynamicServiceFAQ';

const BOOKING_URL = 'https://www.fresha.com/a/ikonomakis-hair-atelier-halandri-irakleitoy-80-bxynmijf?pId=770996';

const FAQ_ITEMS = [
  {
    question: 'Τι είναι τα babylights και πώς διαφέρουν από τα highlights;',
    answer: 'Τα babylights είναι πολύ λεπτές, ίσως 1–3mm, ανταύγειες που εφαρμόζονται σε μικροσκοπικές τούφες μέσα σε foils. Μιμούνται τις φυσικές ανταύγειες που βγαίνουν στα μαλλιά των παιδιών από τον ήλιο — εξ ου και το όνομα. Τα κλασικά highlights είναι πιο χοντρές τούφες με πιο ορατό αποτέλεσμα.',
  },
  {
    question: 'Σε ποιον ταιριάζουν τα babylights;',
    answer: 'Τα babylights ταιριάζουν σε όποιον θέλει λεπτό, φυσικό multidimensional αποτέλεσμα — ιδιαίτερα σε φυσικά ξανθά ή ανοιχτά καστανά μαλλιά. Είναι η κατεξοχήν τεχνική για "beachy, sun-kissed" αποτέλεσμα. Λειτουργούν επίσης εξαιρετικά ως συμπλήρωμα πάνω σε balayage ή airtouch για εξαιρετική διαστρωμάτωση.',
  },
  {
    question: 'Πόσο κοστίζουν τα babylights στο Χαλάνδρι;',
    answer: 'Τα babylights στο Ikonomakis Hair Atelier ξεκινούν από €150. Λόγω της λεπτομέρειας και του χρόνου που απαιτείται (κάθε τούφα είναι μόλις 1–3mm), η τιμή αντανακλά την αυξημένη τεχνική απαίτηση. Η τιμή περιλαμβάνει consultation, εφαρμογή, toner και styling.',
  },
  {
    question: 'Κάθε πότε χρειάζονται ανανέωση τα babylights;',
    answer: 'Η ανανέωση των babylights προγραμματίζεται συνήθως κάθε 3–5 μήνες, ανάλογα με τη φυσική βάση, την ένταση του ξανοίγματος και την ανάπτυξη της ρίζας. Ένα ενδιάμεσο gloss μπορεί να ανανεώσει τον τόνο· ο τεχνίτης επιβεβαιώνει το προσωπικό πλάνο συντήρησης.',
  },
];

export default function BabylightsHubPage() {
  useEffect(() => {
    clearJsonLd();
    getSiteSchemas().forEach(addJsonLd);
    addJsonLd(HAIRSALON_SCHEMA);
    addJsonLd(buildServiceSchema({ name: 'Babylights Χαλάνδρι', description: 'Babylights — λεπτές, φυσικές ανταύγειες για multidimensional, sun-kissed αποτέλεσμα. Master Color Specialists, Ikonomakis Hair Atelier Χαλάνδρι.', priceFrom: 150, url: '/services/babylights' }));
    addJsonLd({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://ikonomakis.gr/' },
        { '@type': 'ListItem', position: 2, name: 'Υπηρεσίες', item: 'https://ikonomakis.gr/services' },
        { '@type': 'ListItem', position: 3, name: 'Babylights Χαλάνδρι', item: 'https://ikonomakis.gr/services/babylights' },
      ],
    });

  }, []);

  return (
    <div className="bg-cream-bg text-warm-black min-h-screen">
      <TopBar />
      <Nav />

      {/* Breadcrumb */}
      <div className="px-[6vw] py-4 bg-cream-soft/40 border-b border-brown/10">
        <div className="max-w-container mx-auto text-tiny text-muted/80 flex gap-2 flex-wrap">
          <a href="/" className="hover:text-warm-black">Home</a><span>/</span>
          <a href="/services" className="hover:text-warm-black">Υπηρεσίες</a><span>/</span>
          <span className="text-warm-black">Babylights</span>
        </div>
      </div>

      <main>
        {/* Hero */}
        <section className="px-[6vw] pt-[80px] pb-[70px] bg-ivory">
          <div className="max-w-container mx-auto grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <EyebrowPill variant="dark" className="mb-6">Babylights · Χαλάνδρι</EyebrowPill>
              <h1 className="font-serif text-display-l text-warm-black font-medium tracking-[-2px] leading-[0.95] mb-6">
                Babylights —<br /><em className="italic text-gold">η λεπτή λεπτομέρεια.</em>
              </h1>
              <p className="text-body-l text-muted leading-[1.65] max-w-[540px] mb-8">
                Οι πιο λεπτές ανταύγειες που μπορείτε να φτιάξετε — 1 έως 3mm ανά τούφα. Φυσικό, multidimensional sun-kissed αποτέλεσμα που μιμείται τη φωτεινότητα της παιδικής ηλικίας.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 rounded-pill bg-warm-black text-cream font-medium text-body-s hover:bg-brown transition-colors">
                  Κράτηση Online →
                </a>
                <Link to="/services/hair-color" className="inline-flex items-center gap-2 px-8 py-4 rounded-pill border border-brown/30 text-warm-black font-medium text-body-s hover:border-warm-black transition-colors">
                  Όλες οι υπηρεσίες χρώματος
                </Link>
              </div>
            </div>
            <img
              src="https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?auto=format&fit=crop&w=1200&q=80"
              alt="Babylights — λεπτές φυσικές ανταύγειες σε ξανθά μαλλιά, Ikonomakis Hair Atelier Χαλάνδρι"
              className="w-full rounded-card-l object-cover"
              style={{ aspectRatio: '4/3' }}
              loading="eager"
            />
          </div>
        </section>

        {/* Τι είναι */}
        <section className="px-[6vw] py-[90px] bg-cream-bg">
          <div className="max-w-reading mx-auto">
            <EyebrowPill variant="gold-soft" className="mb-6">Η Τεχνική</EyebrowPill>
            <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] mb-6">Τι είναι τα babylights;</h2>
            <div className="space-y-5 text-body-l text-muted leading-[1.75]">
              <p>
                Τα <strong className="text-warm-black">babylights</strong> είναι μια εξαιρετικά λεπτομερής τεχνική ανταυγειών που χρησιμοποιεί μικροσκοπικές τούφες μαλλιών — μόλις 1 έως 3 χιλιοστά πλάτους — τοποθετημένες σε foils σε κοντινές αποστάσεις μεταξύ τους. Το αποτέλεσμα μιμείται ακριβώς τη φυσική φωτεινότητα που δημιουργεί ο ήλιος στα μαλλιά, ιδιαίτερα στα μαλλιά των παιδιών — εξ ου και το όνομα "baby lights".
              </p>
              <p>
                Η διαφορά από τα κλασικά highlights είναι θεαματική: τα babylights δίνουν ένα <strong className="text-warm-black">multidimensional, σχεδόν αόρατο αποτέλεσμα</strong> όπου κανείς δεν μπορεί να διακρίνει πού είναι το χρώμα και πού το φυσικό μαλλί. Δεν υπάρχουν ορατές "ρίγες" ή "ζωνάρια" χρώματος — μόνο ζωντανή, διαστρωματωμένη φωτεινότητα.
              </p>
              <p>
                Λόγω της πολυπλοκότητάς τους, τα babylights <strong className="text-warm-black">απαιτούν περισσότερο χρόνο</strong> από τις άλλες τεχνικές (3–5 ώρες) και υψηλή τεχνική κατάρτιση — στο Ikonomakis Hair Atelier αναλαμβάνονται αποκλειστικά από Color Experts και Master Color Specialists.
              </p>
            </div>
          </div>
        </section>

        {/* Σε ποιον ταιριάζει */}
        <section className="px-[6vw] py-[80px] bg-cream-soft">
          <div className="max-w-container mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-start">
            <div>
              <EyebrowPill variant="gold-soft" className="mb-6">Υποψηφιότητα</EyebrowPill>
              <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] mb-6">Σε ποιον ταιριάζουν;</h2>
              <div className="space-y-4 text-body text-muted leading-[1.75]">
                <p>Τα babylights ταιριάζουν ιδανικά σε:</p>
                <ul className="space-y-3">
                  {[
                    'Φυσικά ξανθά ή ανοιχτά καστανά μαλλιά που θέλουν "sun-kissed" αποτέλεσμα',
                    'Μαλλιά που έχουν ήδη balayage και θέλουν extra διαστρωμάτωση',
                    'Όποιον θέλει πολύ φυσικό, αδιόρατο αποτέλεσμα',
                    'Μαλλιά κατάλληλα για ελεγχόμενο ξάνοιγμα, μετά από αξιολόγηση της κατάστασης και του ιστορικού χρώματος',
                    'Πελάτες που θέλουν "glass hair" εφέ με φυσικό multidimensional χρώμα',
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-3">
                      <span className="text-gold mt-0.5 flex-shrink-0">✦</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="space-y-5">
              <h3 className="font-serif text-h2 text-warm-black font-medium">Babylights vs άλλες τεχνικές</h3>
              <div className="overflow-hidden rounded-card border border-brown/10">
                <table className="w-full text-body-s">
                  <thead>
                    <tr className="bg-warm-black text-cream">
                      <th className="text-left px-5 py-3 font-medium">Τεχνική</th>
                      <th className="text-left px-5 py-3 font-medium">Αποτέλεσμα</th>
                      <th className="text-left px-5 py-3 font-medium">Τιμή από</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ['Babylights', 'Πολύ φυσικό, multidimensional', '€150'],
                      ['Balayage', 'Φυσικό gradient, ορατό', '€150'],
                      ['Airtouch', 'Διάχυτο, soft gradient', '€200'],
                      ['Highlights', 'Ορατές ρίγες, graphic', '€100'],
                      ['Tizzy Lights', 'Bright, editorial', '€150'],
                    ].map(([tec, res, price], i) => (
                      <tr key={tec} className={i % 2 === 0 ? 'bg-ivory' : 'bg-cream-bg'}>
                        <td className="px-5 py-3 text-warm-black font-medium">{tec}</td>
                        <td className="px-5 py-3 text-muted">{res}</td>
                        <td className="px-5 py-3 text-warm-black font-medium">{price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-tiny text-muted">
                Δεν ξέρεις ποια να επιλέξεις; Η{' '}
                <Link to="/services/face-metrics-pro" className="text-gold hover:text-gold-dark underline underline-offset-2">
                  consultation Face Metrics Pro
                </Link>{' '}
                ή ένα απλό consultation με τον ειδικό σου θα απαντήσει.
              </p>
            </div>
          </div>
        </section>

        {/* Διαδικασία + Τιμή + Συντήρηση */}
        <section className="px-[6vw] py-[90px] bg-cream-bg">
          <div className="max-w-reading mx-auto">
            <EyebrowPill variant="gold-soft" className="mb-6">Διαδικασία & Τιμή</EyebrowPill>
            <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] mb-8">Τι να περιμένετε</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-12">
              {[
                { step: '01', title: 'Consultation & Ανάλυση', text: 'Αναλύουμε τη χρωματική βάση, την ιστορία και τον τύπο μαλλιού για να σχεδιάσουμε το σωστό pattern τοποθέτησης.' },
                { step: '02', title: 'Εφαρμογή Foils', text: 'Κάθε λεπτή τούφα τοποθετείται σε foil. Το συνολικό ραντεβού, μαζί με consultation, toner και styling, διαρκεί συνήθως 3–5 ώρες.' },
                { step: '03', title: 'Toner & Finishing', text: 'Toner για ομοιόμορφο, ζεστό ή cool αποτέλεσμα, glossing για λάμψη και τελικό styling included.' },
              ].map((s) => (
                <div key={s.step} className="p-6 bg-ivory rounded-card border border-brown/10">
                  <div className="font-serif text-[36px] text-gold/30 font-medium leading-none mb-3">{s.step}</div>
                  <h3 className="font-semibold text-warm-black text-body-s mb-2">{s.title}</h3>
                  <p className="text-tiny text-muted leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
              {[
                { label: 'Τιμή από', value: '€150' },
                { label: 'Ενδεικτική διάρκεια', value: '3–5 ώρες' },
                { label: 'Συντήρηση', value: 'κάθε 3–5 μήνες' },
              ].map((info) => (
                <div key={info.label} className="p-6 rounded-card-xl bg-warm-black text-center">
                  <div className="text-tiny uppercase tracking-widest text-cream/60 mb-1">{info.label}</div>
                  <div className="font-serif text-[32px] text-gold-light font-medium">{info.value}</div>
                </div>
              ))}
            </div>
            <div className="space-y-4 text-body text-muted leading-[1.75]">
              <h3 className="font-serif text-h2 text-warm-black">Συντήρηση babylights στο σπίτι</h3>
              <p>
                Τα babylights, λόγω της λεπτής εφαρμογής, είναι πιο ευάλωτα στη θερμότητα. Αποφύγετε τη χρήση λαμαρίνας/curler σε πολύ υψηλή θερμοκρασία και χρησιμοποιείτε <strong className="text-warm-black">θερμοπροστασία σε κάθε styling</strong>. Σαμπουάν και conditioner για βαμμένα μαλλιά είναι απαραίτητα.
              </p>
              <p>
                Για παράταση της ζωής του αποτελέσματος, προτείνουμε{' '}
                <Link to="/services/hair-treatments" className="text-gold hover:text-gold-dark underline underline-offset-2">θεραπεία K18 ή glossing</Link>{' '}
                ενδιάμεσα — τα babylights μπορούν να συνδυαστούν εξαιρετικά και με{' '}
                <Link to="/milbon-360-chalandri" className="text-gold hover:text-gold-dark underline underline-offset-2">Milbon 360° θεραπεία</Link>{' '}
                για μέγιστη λάμψη και αναδόμηση.
              </p>
            </div>
          </div>
        </section>

        {/* Πριν / Μετά */}
        <section className="px-[6vw] py-[70px] bg-cream-soft">
          <div className="max-w-container mx-auto">
            <EyebrowPill variant="gold-soft" className="mb-6">Αποτελέσματα</EyebrowPill>
            <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] mb-8">Babylights — αποτέλεσμα και έμπνευση</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
              {[
                { src: 'https://images.unsplash.com/photo-1527799820374-dcf8d9d4a388?auto=format&fit=crop&w=800&q=80', caption: 'Έμπνευση — ενδεικτική φωτογραφία, όχι εργασία του atelier', alt: 'Ενδεικτική φωτογραφία για έμπνευση φυσικού χρώματος μαλλιών' },
                { src: 'https://media.base44.com/images/public/69d96446735f4e1f0f7d5bbf/36bc2da29_IMG_7188.jpg', caption: 'Babylights + Glossing αποτέλεσμα', alt: 'Babylights σε καστανά μαλλιά με glossing — φυσική λάμψη και διαστρωμάτωση χρώματος' },
                { src: 'https://images.unsplash.com/photo-1526045612212-70caf35c14df?auto=format&fit=crop&w=800&q=80', caption: 'Έμπνευση φωτεινότητας — ενδεικτική φωτογραφία, όχι εργασία του atelier', alt: 'Ενδεικτική φωτογραφία για έμπνευση φωτεινότητας μαλλιών' },
              ].map((img) => (
                <div key={img.src} className="rounded-card overflow-hidden">
                  <img src={img.src} alt={img.alt || img.caption} className="w-full object-cover" style={{ aspectRatio: '3/4' }} loading="lazy" />
                  <div className="px-4 py-3 bg-ivory text-tiny text-muted">{img.caption}</div>
                </div>
              ))}
            </div>
            <div className="mt-6">
              <Link to="/before-after" className="inline-flex items-center gap-2 text-body-s text-gold hover:text-gold-dark transition-colors underline underline-offset-2">
                Δείτε όλες τις μεταμορφώσεις μαλλιών →
              </Link>
            </div>
          </div>
        </section>

        {/* Journal links */}
        <section className="px-[6vw] py-[60px] bg-ivory">
          <div className="max-w-container mx-auto">
            <h3 className="font-serif text-h2 text-warm-black font-medium mb-6">Σχετικά άρθρα από το Journal</h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { label: 'Balayage vs highlights — ποιο να επιλέξεις;', href: '/balayage-vs-highlights' },
                { label: 'Πώς να διατηρήσεις το χρώμα των μαλλιών σου', href: '/pos-na-diatirisis-to-xroma-sou' },
                { label: 'Τι είναι το hair glossing και πότε χρειάζεται;', href: '/ti-einai-to-hair-glossing' },
              ].map((article) => (
                <Link key={article.href} to={article.href} className="p-5 rounded-card bg-cream-bg border border-brown/10 hover:border-gold/30 transition-colors group">
                  <span className="text-tiny uppercase tracking-widest text-gold mb-2 block">Journal</span>
                  <span className="font-serif text-[17px] text-warm-black group-hover:text-gold transition-colors leading-snug">{article.label}</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="px-[6vw] py-[90px] bg-cream-bg">
          <div className="max-w-reading mx-auto">
            <EyebrowPill variant="dark" className="mb-6">Συχνές Ερωτήσεις</EyebrowPill>
            <h2 className="font-serif text-display-s text-warm-black font-medium tracking-[-1.5px] mb-8">Babylights — τι θέλεις να ξέρεις</h2>
            <DynamicServiceFAQ path="/services/babylights" fallback={FAQ_ITEMS} />
            <div className="mt-8">
              <Link to="/faq" className="text-body-s text-gold hover:text-gold-dark underline underline-offset-2 transition-colors">
                Δείτε όλες τις συχνές ερωτήσεις →
              </Link>
            </div>
          </div>
        </section>

        {/* Related */}
        <section className="px-[6vw] py-[60px] bg-cream-soft">
          <div className="max-w-container mx-auto">
            <h3 className="font-serif text-h2 text-warm-black font-medium mb-6">Σχετικές υπηρεσίες</h3>
            <div className="flex flex-wrap gap-3">
              {[
                { label: 'Balayage Χαλάνδρι', href: '/balayage-chalandri' },
                { label: 'Airtouch', href: '/services/airtouch' },
                { label: 'Tizzy Lights', href: '/tizzy-lights-chalandri' },
                { label: 'Blonde Specialist', href: '/blonde-specialist-chalandri' },
                { label: 'K18 Treatment', href: '/k18-hair-treatment-chalandri' },
                { label: 'Milbon 360°', href: '/milbon-360-chalandri' },
                { label: 'Όλο το χρώμα →', href: '/services/hair-color' },
              ].map((item) => (
                <Link key={item.href} to={item.href} className="px-5 py-2.5 rounded-pill border border-brown/20 text-tiny text-warm-black hover:border-warm-black transition-colors">
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="px-[6vw] py-[90px] bg-cream-bg">
          <FinalCTACard
            title="Κλείστε"
            titleEm="Babylights."
            description="Η πιο λεπτομερής τεχνική ανταυγειών — από Master Color Specialists στο Ikonomakis Hair Atelier Χαλάνδρι. Ηρακλείτου 80 · 211 800 4132."
            ctaText="Κράτηση Online →"
            ctaHref={BOOKING_URL}
            ctaTarget="_blank"
            ctaRel="noopener noreferrer"
          />
        </section>
      </main>

      <Footer />
    </div>
  );
}